import { fetchJson } from '@api/ApiHelper';

// Flag to enable mock responses for testing
const USE_MOCK_RESPONSES = false; // Set to false to use real API calls

export interface FaceBoundingBoxResponse {
  width: number;
  height: number;
  left: number;
  top: number;
}

export interface AttachmentFaceResponse {
  id: string;
  boundary_id: string;
  source_id: string;
  source_url: string;
  created_at: string;
  updated_at: string;
  identity_id: string;
  aws_face_id: string;
  confidence: number;
  bounding_box: FaceBoundingBoxResponse;
  identity_name?: string;
}

export interface BrandfolderFaceResponse {
  id: string;
  source_url: string;
  identity_name: string;
  bounding_box: FaceBoundingBoxResponse;
}

export interface GetBrandfolderFacesOptions {
  brandfolderKey: string;
}

// Mock data for testing
const MOCK_ASSET_URL =
  'https://cdn.fs.brandfolder.com/Gvhj4cnuSm2pNVyk3XtB/convert?fit=max&format=jpeg&h=162&' +
  'policy=eyJjYWxsIjpbImNvbnZlcnQiLCJyZWFkIl0sImV4cGlyeSI6MTc2MDk1NDAzOCwiaGFuZGxlIjoi' +
  'R3ZoajRjbnVTbTJwTlZ5azNYdEIifQ%3D%3D&rotate=exif&signature=' +
  'e058382cf27321bc3c80c561db480e377128c8d6efdcfd78387ce0426cf67a91&strip=false&w=262';

const mockFacesResponse: AttachmentFaceResponse[] = [
  {
    id: 'mock-face-1',
    boundary_id: 'mock-boundary-1',
    source_id: 'mock-source-1',
    source_url: MOCK_ASSET_URL,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    identity_id: 'mock-identity-1',
    aws_face_id: 'mock-aws-face-1',
    confidence: 99.99,
    bounding_box: {
      width: 0.17338751256465912,
      height: 0.34844306111335754,
      left: 0.3103182911872864,
      top: 0.11187176406383514
    },
    identity_name: 'Ross Geller' // Named face
  },
  {
    id: 'mock-face-2',
    boundary_id: 'mock-boundary-2',
    source_id: 'mock-source-2',
    source_url: MOCK_ASSET_URL,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    identity_id: 'mock-identity-2',
    aws_face_id: 'mock-aws-face-2',
    confidence: 98.5,
    bounding_box: {
      width: 0.3297042846679688,
      height: 0.3827564716339111,
      left: 0.3810241222381592,
      top: 0.175951361656189
    },
    identity_name: '' // Unnamed face
  },
  {
    id: 'mock-face-3',
    boundary_id: 'mock-boundary-3',
    source_id: 'mock-source-3',
    source_url: MOCK_ASSET_URL,
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-01T00:00:00Z',
    identity_id: 'mock-identity-3',
    aws_face_id: 'mock-aws-face-3',
    confidence: 97.2,
    bounding_box: {
      width: 0.25,
      height: 0.35,
      left: 0.1,
      top: 0.2
    }
    // No identity_name - unnamed face
  }
];

const mockIdentitiesResponse: IdentityResponse[] = [
  {
    id: 'identity-1',
    name: 'Tayler Reed',
    face_uri: 'https://example.com/placeholder.jpg',
    tag_count: 5
  },
  {
    id: 'identity-2',
    name: 'Alex Johnson',
    face_uri: 'https://example.com/placeholder2.jpg',
    tag_count: 3
  },
  {
    id: 'identity-3',
    name: 'Jamie Smith',
    face_uri: 'https://example.com/placeholder3.jpg',
    tag_count: 8
  },
  {
    id: 'identity-4',
    name: 'Casey Brown',
    face_uri: 'https://example.com/placeholder4.jpg',
    tag_count: 2
  },
  {
    id: 'identity-5',
    name: 'Ross Geller',
    face_uri: 'https://example.com/placeholder5.jpg',
    tag_count: 12
  }
];

export interface GetAttachmentFacesOptions {
  attachmentKey: string;
  brandfolderKey: string;
}

export interface UpdateFaceNameOptions {
  faceId: string;
  brandfolderKey: string;
  name: string;
}

export interface DeleteFaceNameOptions {
  faceId: string; // Required for API compatibility, but faceIds array takes precedence
  attachmentKey: string;
  brandfolderKey: string;
  faceIds?: string[]; // Optional array of face IDs to batch delete
}

export interface IdentityResponse {
  id: string;
  name: string;
  face_uri?: string;
  tag_count?: number;
}

export interface GetIdentitiesOptions {
  brandfolderKey: string;
}

// Request 1: Get faces for an attachment
export const getAttachmentFaces = async (
  options: GetAttachmentFacesOptions
): Promise<AttachmentFaceResponse[]> => {
  const { attachmentKey, brandfolderKey } = options;

  if (USE_MOCK_RESPONSES) {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    return Promise.resolve(mockFacesResponse);
  }

  return fetchJson<AttachmentFaceResponse[]>({
    url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/faces/attachments/${attachmentKey}/faces`
  });
};

// Request 2: Update face name
export const updateFaceName = async (
  options: UpdateFaceNameOptions
): Promise<void> => {
  const { faceId, brandfolderKey, name } = options;

  if (USE_MOCK_RESPONSES) {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));
    console.log(`[MOCK] Updating face ${faceId} with name: ${name}`);
    return Promise.resolve();
  }

  return fetchJson<void>({
    url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/faces/${faceId}`,
    method: 'PUT',
    body: { name }
  });
};

// Request 3: Delete face(s)
export const deleteFaceName = async (
  options: DeleteFaceNameOptions
): Promise<void> => {
  const { faceId, attachmentKey, brandfolderKey, faceIds } = options;

  // Use faceIds array if provided, otherwise use single faceId
  const facesToDelete = faceIds && faceIds.length > 0 ? faceIds : [faceId];

  if (USE_MOCK_RESPONSES) {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 300));
    console.log(`[MOCK] Deleting faces ${facesToDelete.join(', ')} from attachment ${attachmentKey}`);
    return Promise.resolve();
  }

  // Pass face_ids array in request body to specify which faces to delete
  return fetchJson<void>({
    url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/faces/attachments/${attachmentKey}/faces`,
    method: 'DELETE',
    body: { face_ids: facesToDelete }
  });
};

// Request 4: Get identities for dropdown
export const getIdentities = async (
  options: GetIdentitiesOptions
): Promise<IdentityResponse[]> => {
  const { brandfolderKey } = options;

  if (USE_MOCK_RESPONSES) {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 400));
    return Promise.resolve(mockIdentitiesResponse);
  }

  return fetchJson<IdentityResponse[]>({
    url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/identities`
  });
};

export const getBrandfolderFaces = async (
  options: GetBrandfolderFacesOptions
): Promise<BrandfolderFaceResponse[]> => {
  const { brandfolderKey } = options;

  return fetchJson<BrandfolderFaceResponse[]>({
    url: `/api/v4/private/people_tagging/brandfolders/${brandfolderKey}/faces`,
    method: 'GET',
  });
};
